import type {
  FleetActivityDescription,
  FleetConfigResponse,
  FleetKindDescription,
  FleetRun,
  FleetRunPlace
} from './claude-fleet.types'
import type { FleetRunOutcome, FleetRunTriggerKind } from './claude-fleet.runs'

/**
 * Plain-language names and explanations for what the fleet does. The fleet sends its own (`describe`
 * and `kinds` on GET /fleet/config) and those win; the text here is what the page shows until a fleet service
 * that sends them is deployed, and for anything it leaves out.
 */

export interface FleetActivityText {
  title: string
  summary: string
  details: string
}

const LOCAL_ACTIVITIES: Record<string, FleetActivityText> = {
  'backlog-triage': {
    title: 'Backlog triage',
    summary: 'Checks old Jira tickets and says whether each is still valid, fixed or obsolete.',
    details: 'Picks a few tickets a day, reads the code they point at and comments on the ticket. It can cancel a ticket that is clearly obsolete; each cancel is listed in #ai-cancelled-tickets with an Undo button.'
  },
  'lpe-loop': {
    title: 'Delivery lane',
    summary: 'Takes easy tickets from To Do and delivers them as reviewed pull requests.',
    details: 'Each tick looks after the pull requests the lane already opened, then may start one new delivery. A delivery researches the ticket, builds the change, tests it and opens a PR for a person to merge. When it needs a decision it asks on the ticket and moves on.'
  },
  'qa-verify': {
    title: 'QA verification',
    summary: 'Tests tickets in dev or staging once their change is deployed, and moves the ones that pass.',
    details: 'AgentCore takes tickets the delivery lane delivered. Runners take every other ticket; those wait for a runner and never run on AgentCore.'
  },
  'pr-loop': {
    title: 'PR reviews and fixes',
    summary: 'Reviews pull requests when they change, and fixes review findings on the ones it may fix.',
    details: 'Reviews and fixes the pull requests the fleet opened, plus those of the engineers listed here. Each PR gets a few automatic rounds a day at most.'
  },
  'pr-review': {
    title: 'PR review',
    summary: 'One review of one pull request.',
    details: 'Started by the PR watcher when a pull request changes, or by a person.'
  },
  'pr-fix': {
    title: 'PR fix',
    summary: 'Fixes the open review findings on one pull request and replies on each thread.',
    details: 'Started when a review posts findings on a PR the fleet may fix.'
  },
  'local-pr-review': {
    title: 'PR review (own queue)',
    summary: 'One review run from this runner\'s own queue, not for the fleet.',
    details: 'A /pr-loop or /runner window ran it as a background agent, or the machine started it on its own. It shows only in this runner\'s history and its Runs list.'
  },
  'local-pr-fix': {
    title: 'PR fix (own queue)',
    summary: 'One fix run from this runner\'s own queue, not for the fleet.',
    details: 'A /pr-loop or /runner window ran it as a background agent, or the machine started it on its own. It shows only in this runner\'s history and its Runs list.'
  },
  'local-qa-verify': {
    title: 'QA verification (own queue)',
    summary: 'One QA check run from this runner\'s own queue, not for the fleet.',
    details: 'The runner\'s minute pass started it as a one-shot, from its watcher or a /runner window. It shows only in this runner\'s history and its Runs list.'
  },
  'local-mention-sweep': {
    title: '@Claude mentions (own queue)',
    summary: 'One @Claude mention pass run from this runner\'s own queue, not for the fleet.',
    details: 'The runner\'s minute pass started it as a one-shot, from its watcher or a /runner window. It shows only in this runner\'s history and its Runs list.'
  },
  'mention-sweep': {
    title: '@Claude mentions',
    summary: 'Answers @Claude mentions in Jira, Bitbucket and Slack.',
    details: 'Each mention is answered once, in the thread it came from.'
  },
  talk: {
    title: 'Talk it over',
    summary: 'A chat with Claude about one thing on an ADX page, such as a decision waiting on someone, that reads but never changes anything.',
    details: 'Talk it over and Ask Claude open beside a card on the Claude pages. Claude reads the ticket, its pull requests, the code and the run, and answers with links to what it read. Nothing changes until a person clicks a button. It runs on Bedrock inside the fleet service, not on a runner.'
  },
  'laptop-runners': {
    title: 'Runners',
    summary: 'Engineers\' machines that take fleet work, so it does not run on AgentCore.',
    details: 'An engineer makes their computer a runner with /runner. These settings are the ceiling for every runner: each keeps its own settings within them. Turning this off stops offers to every runner at once.'
  },
  'release-watch': {
    title: 'Release watcher',
    summary: 'Marks a release done in Jira once every repo in it is live in prod, then starts prod verification.',
    details: 'Watches prod deploys against the Jira release schedule. Clicking Release in Jira by hand still works. The release owner gets a DM when a repo is stuck or no runner is free.'
  },
  'release-staging-qa': {
    title: 'Release staging QA',
    summary: 'Tests every ticket in the release on staging once the release is there, and moves the ones that pass to Done in Staging.',
    details:
      'Every 5 minutes it checks whether each repo in the newest cut release is running it on staging. Once all are, each ticket not yet Done in Staging is offered to the open runners, each taking tickets within its own slots, so more runners finish sooner. A failed or blocked ticket is left for a person. The release owner gets one DM when it starts and one when it finishes.'
  },
  'fixme-sweep': {
    title: 'Playwright suite sweep',
    summary: 'Keeps the nightly Playwright run green: quarantines new failures and restores quarantined tests that pass again.',
    details:
      'Once a day an open runner re-runs last night\'s failing tests and the quarantined ones on the environment the nightly used, with no browser window on screen. It opens one PR with the quarantine changes, fixes outdated tests as draft PRs when Fix outdated tests is on, and posts a summary in #dev-qa. Runners only.'
  },
  'lane-size': {
    title: 'Size the delivery queue',
    summary: 'Labels tickets in the delivery queue as easy, medium or hard, and how much they matter, before a lane reaches them, and fills in severity, reach and location count where no one has.',
    details:
      'A runner runs the lane\'s own difficulty check on new tickets soon after they are filed, and on a few older ones a day, top of the queue first. It writes the labels the lane would write itself and, in the same change, a severity, reach or location count still at Jira\'s default. Each change emails the ticket\'s watchers, which is why older tickets go slowly.'
  },
  cover: {
    title: 'Fill test coverage gaps',
    summary: 'Writes the missing unit and Playwright tests for open coverage gaps, across every repo, and opens draft pull requests for them.',
    details:
      'A runner takes the most severe open coverage gaps from the findings lake, a few a run, and writes the tests each one is missing. It changes no application code. Each branch must pass a test-only check first: only test files changed, no test skipped or weakened, at least one new assertion, and every new test shown to fail against deliberately broken code. What passes becomes a draft pull request for the normal review loop. A gap that needs new test infrastructure or an application change is set aside for a person. Runners only.'
  },
  'qa-fields-audit': {
    title: 'QA fields audit',
    summary: "Keeps the QA dashboard's numbers right by filling and correcting the QA fields on Jira tickets.",
    details:
      'At each run time an open runner reads the current and previous releases\' tickets, fills a missing QA Assignee from the comments and status history, fills missing QA Failure Reasons from the actual failure comments, and lowers an inflated QA Failure Count to the number of failed rounds the ticket shows. Every value is traced to a quote or a status change on the ticket. Jira only, no browser. Runners only.'
  },
  'release-verify': {
    title: 'Release verification',
    summary: 'Tests a shipped release in production and moves each ticket that passes to Done in Production.',
    details: 'Read-only in prod, with writes only to the one allowlisted test merchant.'
  },
  'my-sweep': {
    title: 'My sweep',
    summary: 'Tidies one engineer\'s own tickets and pull requests, and DMs them what it did.',
    details: 'Started by the engineer from My work.'
  },
  'cost-digest': {
    title: 'Cost digest',
    summary: 'Sends the operators a daily line of yesterday\'s runs and the month\'s spend against the budget.',
    details: 'Weekday mornings. Counts each activity\'s runs, outcomes and model cost, and adds the AWS bill so far. Starts no Claude session.'
  },
  'weekly-digest': {
    title: 'Weekly fleet digest',
    summary: 'Writes the last week\'s numbers to the Claude fleet weekly page and this page\'s This week, and posts one line in #dev-qa.',
    details: 'Mondays 9:00 AM ET, with no model: pull requests opened and merged, reviews, fixes, QA, decisions, deliveries, failures new this week, where the work ran and what it cost. Until the stack is deployed with weeklyDigest=publish (or the SSM parameter says publish), the page stays an unpublished draft and nothing is posted.'
  },
  judge: {
    title: 'Run judge',
    summary: 'Grades a sample of yesterday\'s finished runs for how well the work was done.',
    details: 'Once a day it reads each sampled run\'s transcript and grades it against a written rubric, 1 to 5, onto the run record. It never acts. It calls a model on Bedrock but starts no Claude session.'
  },
  scorer: {
    title: 'Outcome scorer',
    summary: 'Checks whether each recent run\'s work held up: answered, not undone, merged, shipped.',
    details: 'Once a day, with no model: a mention answered without a second ask, a cancel nobody undid, a delivery that reached Done, a fixed PR that merged. The verdict goes on the run record.'
  },
  'pr-watch': {
    title: 'PR watcher',
    summary: 'Notices pull request pushes and review findings, and queues reviews and fixes.',
    details: 'Every minute, with no model: a push queues a review, a finding queues a fix, for the PRs the PR settings cover. Runners\' own watchers keep their engineers\' PRs.'
  },
  bookkeeping: {
    title: 'Bookkeeping',
    summary: 'Keeps the Slack feeds, the quality snapshots and the delivery metrics up to date.',
    details: 'A short runner job with no model, so it starts no Claude session. The feeds and inbox jobs do their work only while the fleet setting Bookkeeping run by is Gateway; otherwise runners do it.'
  },
  'bookkeeping-inboxes': {
    title: 'Bookkeeping',
    summary: 'Every five minutes, does the upkeep that needs no model: decision cards, merged-PR cards, the escape ledger, ownership follow-ups and the daily decisions DM.',
    details: 'Only while the fleet setting Bookkeeping run by is Gateway; otherwise engineers\' laptops do it and this run does nothing. Each item takes a lease, so nothing is done twice. Starts no Claude session.'
  },
  'bookkeeping-feeds': {
    title: 'Bookkeeping',
    summary: 'Every six hours, brings the decisions, PR and cancelled-ticket Slack channels back in line with Jira and Bitbucket.',
    details: 'Runs the #ai-decisions-needed, #ai-approved-prs and #ai-merged-prs, and #ai-cancelled-tickets sweeps, only while the fleet setting Bookkeeping run by is Gateway; otherwise laptops run them. Each sweep takes a fleet-wide lease, so two never post twice. Starts no Claude session.'
  },
  'bookkeeping-quality': {
    title: 'Bookkeeping',
    summary: 'Twice a day, saves the last nightly Playwright runs and the quarantined tests for ADX\'s quality page.',
    details: 'At 7:30 AM and 11:30 PM ET it reads the nightly\'s runs on dev and staging (read-only) and the test.fixme, test.fail and test.skip markers on tattle-playwright\'s main, and writes both to S3. Starts no Claude session.'
  },
  'bookkeeping-metrics': {
    title: 'Bookkeeping',
    summary: 'Once a day, works out the delivery funnel (runs, merged pull requests, production) so ADX can show it without waiting for Confluence.',
    details: 'At 6:45 AM ET it computes delivery-metrics\' model and uploads it to S3 beside its ledger. Starts no Claude session.'
  },
  'bookkeeping-files': {
    title: 'Bookkeeping',
    summary: 'Once a day, deletes the files Claude shared (links, Jira attachments, Slack DM files) once their date has passed.',
    details: 'At 6:00 AM ET. A removed Jira attachment gets one comment saying why. It runs whatever Bookkeeping run by says, since no laptop does it and personal data must not outstay its date. Starts no Claude session.'
  },
  'backstop-review': {
    title: 'Backstop review',
    summary: 'Once each weekday, reviews the open pull requests whose latest push no webhook brought to a review.',
    details: 'Weekdays at 9:00 AM ET it sweeps every open pull request not yet reviewed at its latest commit, so a push whose webhook never arrived still gets a review. Laptop runners only, so it adds nothing to the fleet\'s bill. It follows PR reviews and fixes being on, is skipped while those run on AgentCore, and waits up to a day for a laptop.'
  },
  'backstop-qa': {
    title: 'Backstop QA',
    summary: 'Every two hours on weekdays, tests the tickets waiting in Testing or for a staging check that no status change or deploy sent to QA.',
    details: 'Weekdays from 8:00 AM to 6:00 PM ET it sweeps every ticket in Testing, then shipped tickets on an unreleased version not yet verified on staging, skipping any whose deploy has not changed since it last looked. Laptop runners only. It follows QA verification being on, is skipped while QA runs on AgentCore, and waits up to a day for a laptop.'
  },
  fleet: {
    title: 'Fleet',
    summary: 'Budget, operators, models and browser for the whole fleet.',
    details: 'Operators can change settings, start runs and act on any runner. The monthly budget is checked against the AWS bill.'
  }
}

const LOCAL_KINDS: Record<string, Required<FleetKindDescription>> = {
  'pr-review': { label: 'Reviews', summary: 'Reviews a pull request when it changes.' },
  'pr-fix': { label: 'Fixes', summary: 'Fixes review findings on a pull request.' },
  'mention-sweep': { label: 'Mentions', summary: 'Answers @Claude mentions.' },
  'qa-verify': { label: 'QA', summary: 'Tests a ticket in dev or staging. Needs the VPN.' },
  'release-verify': { label: 'Release verify', summary: 'Tests a shipped release in prod.' },
  'fixme-sweep': { label: 'Test-suite sweeps', summary: 'Re-runs failing and quarantined Playwright tests. Needs the VPN.' },
  'qa-fields-audit': { label: 'QA fields audits', summary: 'Fills and corrects the QA fields on Jira tickets.' },
  cover: { label: 'Coverage gaps', summary: 'Writes missing tests for open coverage gaps and opens draft pull requests.' }
}

export const FLEET_OUTCOME_MEANINGS: Record<FleetRunOutcome, string> = {
  running: 'Still working.',
  ok: 'Finished its work.',
  skipped: 'Started, found nothing to do or was held back by a setting, and ended.',
  stopped: 'A person stopped it.',
  budget: 'Stopped when it reached its budget for one run.',
  error: 'Ended with an error. Open it to see why, or ask for an explanation.'
}

export const FLEET_TRIGGER_MEANINGS: Record<FleetRunTriggerKind, string> = {
  schedule: 'Started by its schedule.',
  adx: 'Started by a person from this page.',
  claude: 'Started by Claude through the MCP connector.',
  'pr-watch': 'Started because a pull request changed.',
  queue: 'Started from a queue: a webhook, a mention or a deploy.',
  lane: 'Started by the delivery lane.',
  manual: 'Started by hand outside this page.',
  inbox: 'Started from this runner\'s own queue: a change to a PR it reviews or fixes.'
}

export const FLEET_PLACE_MEANINGS: Record<FleetRunPlace, string> = {
  agentcore: 'Ran on AgentCore, in AWS.',
  laptop: 'Ran on an engineer\'s runner, using their Claude plan instead of the fleet budget.'
}

/** What an activity is called and what it does: the fleet's words where it sends them. */
/** A key the page has no words for, made readable: `runner-wake` reads "Runner wake". */
export function humanizeKey(key: string): string {
  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_.:]+/g, ' ').trim().toLowerCase()
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : key
}

export function describeActivity(
  activity: string,
  describe: FleetConfigResponse['describe'] | undefined
): FleetActivityText {
  const local = LOCAL_ACTIVITIES[activity]
  const remote: FleetActivityDescription = describe?.[activity] ?? {}
  return {
    title: remote.title || local?.title || humanizeKey(activity),
    summary: remote.summary || local?.summary || '',
    details: remote.details || local?.details || ''
  }
}

/** A setting's label and help text, the fleet's over the page's own. */
export function describeField(
  activity: string,
  field: { key: string; label: string; description?: string },
  describe: FleetConfigResponse['describe'] | undefined
): { label: string; help: string } {
  const remote = describe?.[activity]?.fields?.[field.key]
  return { label: remote?.label || field.label, help: remote?.help || field.description || '' }
}

export function describeKind(
  kind: string,
  kinds: FleetConfigResponse['kinds'] | undefined
): Required<FleetKindDescription> {
  const local = LOCAL_KINDS[kind]
  const remote = kinds?.[kind] ?? {}
  return { label: remote.label || local?.label || kind, summary: remote.summary || local?.summary || '' }
}

/** A run's activity in words, with a lane child's step: "Delivery lane · deliver". */
export function runActivityLabel(
  run: Pick<FleetRun, 'activity' | 'kind'>,
  describe: FleetConfigResponse['describe'] | undefined
): string {
  const title = describeActivity(run.activity ?? '', describe).title || 'Run'
  return run.kind ? `${title} · ${run.kind}` : title
}
